import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from './Logo';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownTimeout, setDropdownTimeout] = useState(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { label: 'Product Development', href: '/services/product-development' },
    { label: 'AI Solutions', href: '/services/ai-solutions' },
    { label: 'Staff Augmentation', href: '/services/staff-augmentation' },
  ];

  const industries = [
    { label: 'Real Estate', href: '/industries/real-estate' },
    { label: 'Fintech', href: '/industries/fintech' },
    { label: 'Wealth Management', href: '/industries/wealth-management' },
    { label: 'Investment Banking', href: '/industries/investment-banking' },
  ];

  const navItems = [
    { label: 'About', href: '/about' },
    { label: 'Services', href: '/services', hasDropdown: true, dropdown: services },
    { label: 'Case Studies', href: '/case-studies' },
    { label: 'Industries', href: '#', hasDropdown: true, dropdown: industries },
  ];

  const openDropdown = (label) => {
    if (dropdownTimeout) { clearTimeout(dropdownTimeout); setDropdownTimeout(null); }
    const item = navItems.find(i => i.label === label);
    if (item?.hasDropdown) setActiveDropdown(label);
  };

  const closeDropdown = () => {
    const t = setTimeout(() => setActiveDropdown(null), 200);
    setDropdownTimeout(t);
  };

  const keepDropdown = () => {
    if (dropdownTimeout) { clearTimeout(dropdownTimeout); setDropdownTimeout(null); }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-slate-100'
          : 'bg-transparent'
      }`}
      style={{ boxShadow: isScrolled ? '0 1px 3px rgba(0,0,0,0.04)' : 'none' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Logo />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => openDropdown(item.label)}
                onMouseLeave={closeDropdown}
              >
                {item.hasDropdown ? (
                  <>
                    <button className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium flex items-center gap-1.5 py-2 cursor-pointer">
                      {item.label}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    {activeDropdown === item.label && (
                      <div className="absolute top-full left-0 pt-2" onMouseEnter={keepDropdown} onMouseLeave={closeDropdown}>
                        <div className="w-56 bg-white border border-slate-100 rounded-xl py-2" style={{ boxShadow: '0 12px 32px rgba(0,0,0,0.08)' }}>
                          {item.dropdown.map((d) => (
                            <Link
                              key={d.label}
                              to={d.href}
                              className="block px-4 py-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors text-sm"
                            >
                              {d.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <Link to={item.href} className="text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium">
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA — gradient button */}
          <div className="hidden md:block">
            <a
              href="https://calendar.app.google/MfHsR1LurH5q2Rii7"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gradient inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white group cursor-pointer"
              style={{ boxShadow: '0 4px 16px rgba(38,147,255,0.25)' }}
            >
              Schedule a call
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>

          {/* Mobile toggle */}
          <button className="md:hidden text-slate-700 p-2 cursor-pointer" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-100">
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <div key={item.label}>
                {item.hasDropdown ? (
                  <div>
                    <div className="flex items-center justify-between text-slate-700 font-medium py-2">{item.label}<ChevronDown className="w-4 h-4" /></div>
                    <div className="pl-4 space-y-2 mt-1">
                      {item.dropdown.map((d) => (
                        <Link key={d.label} to={d.href} className="block text-slate-500 hover:text-slate-900 text-sm py-1.5" onClick={() => setIsMobileMenuOpen(false)}>
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link to={item.href} className="block text-slate-700 hover:text-slate-900 font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
            <a
              href="https://calendar.app.google/MfHsR1LurH5q2Rii7"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gradient block w-full py-3 rounded-full text-sm font-semibold text-white text-center cursor-pointer"
            >
              Schedule a call
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
